import * as React from "react"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Checkbox - A proper square checkbox control using native input
 * Renders as a visible square with checkmark when checked
 * 
 * iOS Safari Fix:
 * - Native input is positioned absolutely within the tap target (not sr-only)
 * - Input has opacity:0 but still has dimensions for tap handling
 * - This ensures iOS Safari properly receives touch events
 * - Minimum 44x44px tap target for accessibility
 */
const Checkbox = React.forwardRef(({ className, checked, onCheckedChange, disabled, id, ...props }, ref) => {
  // Generate a unique ID if not provided - must be called unconditionally
  const generatedId = React.useId();
  const checkboxId = id || generatedId;

  const handleChange = (e) => {
    if (onCheckedChange) {
      onCheckedChange(e.target.checked);
    }
  };

  return (
    <label 
      htmlFor={checkboxId}
      className={cn(
        // Minimum 44x44px tap target
        "relative inline-flex items-center justify-center",
        "min-h-[44px] min-w-[44px]",
        "cursor-pointer",
        disabled && "cursor-not-allowed opacity-50"
      )}
      // Prevent any parent onClick from interfering
      onClick={(e) => e.stopPropagation()}
    >
      {/* Native checkbox - positioned absolutely with dimensions for iOS tap detection */}
      <input
        ref={ref}
        id={checkboxId}
        type="checkbox"
        checked={checked || false}
        onChange={handleChange}
        disabled={disabled}
        className={cn(
          // Position absolutely over the visual checkbox
          "absolute inset-0 m-auto",
          // Give it size for tap detection (iOS needs this)
          "w-[44px] h-[44px]",
          // Invisible but tappable
          "opacity-0",
          // Ensure it receives touches
          "cursor-pointer",
          "z-10"
        )}
        style={{
          // Ensure iOS can tap it
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation'
        }}
        {...props}
      />
      {/* Custom visual checkbox - positioned in center */}
      <div
        className={cn(
          // Size - 20x20px square
          "h-5 w-5 shrink-0",
          // Shape - square with slight rounding
          "rounded-[4px]",
          // Border - always visible, 2px for clarity
          "border-2",
          // Conditional styling based on checked state
          checked 
            ? "bg-[#FF6600] border-[#FF6600]" 
            : "bg-white border-zinc-400",
          // Disabled styling
          disabled && "bg-zinc-100 border-zinc-300",
          // Flex for centering checkmark
          "flex items-center justify-center",
          // Transition
          "transition-colors duration-150",
          // No pointer events - let clicks pass to input
          "pointer-events-none",
          className
        )}
      >
        {/* Checkmark - only visible when checked */}
        <Check 
          className={cn(
            "h-3.5 w-3.5 text-white transition-opacity duration-150",
            checked ? "opacity-100" : "opacity-0"
          )} 
          strokeWidth={3} 
        />
      </div>
    </label>
  );
});
Checkbox.displayName = "Checkbox"

export { Checkbox }
